<template>
  <div class="relative mt-4 mb-7">
    <SimpleTable
      class="full-height new-job-tag-table"
      card-wrap
      data-cy="tag-list-card"
    >
      <thead>
        <tr>
          <th>
            <div class="d-flex align-items-center">
              <i class="fa-regular fa-tag mr-2"></i>
              <span class="mr-2">Job Tags</span>
              <div class="number-badge">{{ selectedTags.length }}</div>
              <div class="sera-ml-auto">
                <i
                  v-if="selectedTags.length < maxTags"
                  class="ml-1 align-middle fa-regular fa-square-plus tag-menu-btn"
                  aria-label="Add Tag"
                  @click="toggleTagMenu"
                ></i>
                <TagMenu
                  v-if="showTagMenu"
                  :taggable-type="'job'"
                  :tag-count="selectedTags.length"
                  @close-menu="toggleTagMenu"
                  @add-tag="handleAddTag"
                  @open-tag-modal="toggleNewTagModal"
                />
              </div>
            </div>
          </th>
        </tr>
      </thead>

      <tr class="list-item mt-2">
        <template v-if="selectedTags.length === 0">
          <td>No Tags</td>
        </template>
        <template v-else>
          <td colspan="2">
            <TagList
              :tags="selectedTags"
              :config="tagListConfig"
              @remove-tag="handleRemoveTag"
            />
          </td>
        </template>
      </tr>
    </SimpleTable>
    <TagModal
      v-if="showTagModal"
      :tag="newTag"
      @hide-modal="toggleNewTagModal"
      @save-new-success="handleSaveSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useStore } from "vuex";

import SimpleTable from "@/components/SimpleTable.vue";
import TagList from "@/Tags/TagList.vue";
import TagMenu from "@/Tags/TagMenu.vue";
import TagModal from "@/Tags/TagModal.vue";
import { Tag } from "@/typings/tag";

const store = useStore();
const selectedTags = ref<Tag[]>([]);
const newTag = ref(Object.assign(new Tag(), { taggable_type: "job" }));
const showTagMenu = ref(false);
const showTagModal = ref(false);

const tagListConfig = {
  editable: false,
  removeable: true,
  clickEmit: ""
};

const maxTags = window.currentTenant.tags_config.job.max_allowed;

const toggleTagMenu = () => {
  showTagMenu.value = !showTagMenu.value;
};

const toggleNewTagModal = (label: string) => {
  newTag.value.label = label;
  showTagModal.value = !showTagModal.value;
};

const handleSaveSuccess = (tag: Tag) => {
  toggleNewTagModal(tag.label);
  handleAddTag(tag);
};

const handleAddTag = async (tag: Tag) => {
  if (selectedTags.value.some((t) => t.id === tag.id)) return;
  selectedTags.value.push(tag);
};

const handleRemoveTag = async (tag: Tag) => {
  selectedTags.value = selectedTags.value.filter((t) => t.id !== tag.id);
};

const getFormData = async () => {
  return selectedTags.value.map((tag) => tag.id);
};

defineExpose({ getFormData });
</script>

<style lang="scss" scoped>
.label-text {
  white-space: nowrap;
  color: var(--sera-color-core-blue-cobalt-blue-800);
}

.list-item {
  td {
    white-space: nowrap;
  }

  &.list-item-collapsed {
    visibility: collapse;
  }
}

.info {
  color: #5d5d5d;
}

.info-header {
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
}

.info-icon {
  width: 50px;
  text-align: center;
}

.number-badge {
  display: flex;
  height: 18px;
  padding: 0 4px;
  line-height: 18px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  border: 1px solid var(--sera-color-neutral-gray-med-gray-500);
  background: var(--sera-color-neutral-gray-light-gray-100);
  font-size: 12px;
  font-weight: 600;
}

.tag-menu-btn {
  color: var(--sera-color-text-links);
  font-size: 18px;
  margin-left: auto;
  display: flex;
  justify-content: end;
}
</style>
